import { useState, useEffect } from 'react';
import {
  IconAlertCircle,
  IconRefresh,
  IconTool,
  IconCircleCheck,
  IconCircleX,
  IconLoader2,
} from '@tabler/icons-react';
import api from '../lib/api';
import PageHeader from '../components/PageHeader';
import MainCard from '../components/MainCard';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
} from '@/components/ui/table';
import { Separator } from '@/components/ui/separator';

interface Diagnosis {
  walletICustomer: number;
  apiICustomer: number | null;
  apiSnsi: string | null;
  matches: boolean;
  walletCustomerStatus: string | null;
  apiCustomerStatus: string | null;
  walletAccountNumber: string | null;
  accountBalance: string | null;
  recommendedAction: string;
  actionDetails: string;
  canFix: boolean;
  aiSummary?: string;
  priority?: {
    priority: 'low' | 'medium' | 'high' | 'critical';
    score: number;
    explanation: string;
    factors: string[];
  };
  actionExplanations?: {
    explanations: Array<{ step: number; explanation: string }>;
    overallExplanation: string;
  };
}

export function WalletFailuresPage() {
  const [walletFailures, setWalletFailures] = useState<any[]>([]);
  const [failuresLoading, setFailuresLoading] = useState(false);
  const [failuresError, setFailuresError] = useState('');
  const [diagnosingId, setDiagnosingId] = useState<number | null>(null);
  const [diagnosis, setDiagnosis] = useState<Diagnosis | null>(null);
  const [diagnosisLoading, setDiagnosisLoading] = useState(false);
  const [diagnosisError, setDiagnosisError] = useState('');
  const [fixingId, setFixingId] = useState<number | null>(null);
  const [fixResult, setFixResult] = useState<{ success: boolean; message: string } | null>(null);
  const [stepProgress, setStepProgress] = useState<Array<{
    step: number;
    name: string;
    status: 'pending' | 'in_progress' | 'completed' | 'error' | 'skipped';
    apiResponse?: any;
    error?: string;
  }>>([]);

  const fetchWalletFailures = async () => {
    setFailuresError('');
    setFailuresLoading(true);
    try {
      const res = await api.get('/wallet/failures');
      setWalletFailures(res.data.data || []);
    } catch (err: any) {
      setFailuresError(err.response?.data?.message || err.message || 'Failed to load wallet failures');
      setWalletFailures([]);
    } finally {
      setFailuresLoading(false);
    }
  };

  useEffect(() => {
    fetchWalletFailures();
  }, []);

  const handleDiagnose = async (iCustomer: number, msisdn: string) => {
    setDiagnosingId(iCustomer);
    setDiagnosisLoading(true);
    setDiagnosisError('');
    setDiagnosis(null);
    setFixResult(null);

    try {
      const res = await api.get('/wallet/failures/diagnose/ai', {
        params: {
          i_customer: iCustomer,
          msisdn: msisdn,
        },
      });

      if (res.data.success && res.data.diagnosis) {
        setDiagnosis({
          ...res.data.diagnosis,
          aiSummary: res.data.aiSummary,
          priority: res.data.priority,
          actionExplanations: res.data.actionExplanations,
        });
      } else {
        setDiagnosisError(res.data.message || 'Failed to get diagnosis');
      }
    } catch (err: any) {
      setDiagnosisError(err.response?.data?.message || err.message || 'Failed to diagnose wallet');
    } finally {
      setDiagnosisLoading(false);
      setDiagnosingId(null);
    }
  };

  const handleCloseModal = () => {
    fetchWalletFailures();
    setDiagnosis(null);
    setDiagnosisError('');
    setFixResult(null);
    setFixingId(null);
    setStepProgress([]);
  };

  const handleResolve = async () => {
    if (!diagnosis) return;

    setFixingId(diagnosis.walletICustomer);
    setFixResult(null);

    if (diagnosis.recommendedAction === 'close' && diagnosis.canFix) {
      const initialSteps: Array<{
        step: number;
        name: string;
        status: 'pending' | 'in_progress' | 'completed' | 'error' | 'skipped';
        apiResponse?: any;
        error?: string;
      }> = [
        {
          step: 1,
          name: 'Check Account Balance',
          status: 'pending',
        },
      ];

      if (diagnosis.accountBalance && parseFloat(diagnosis.accountBalance) > 0) {
        initialSteps.push({
          step: 2,
          name: 'Transfer Funds to Holding Account',
          status: 'pending',
        });
        initialSteps.push({
          step: 3,
          name: 'Close Wallet Account',
          status: 'pending',
        });
        initialSteps.push({
          step: 4,
          name: 'Update Database',
          status: 'pending',
        });
      } else {
        initialSteps.push({
          step: 2,
          name: 'Close Wallet Account',
          status: 'pending',
        });
        initialSteps.push({
          step: 3,
          name: 'Update Database',
          status: 'pending',
        });
      }

      setStepProgress(initialSteps);
    } else if (diagnosis.recommendedAction === 'deactivate' && diagnosis.canFix) {
      setStepProgress([
        {
          step: 1,
          name: 'Deactivate Consumer on Silicon',
          status: 'pending',
        },
        {
          step: 2,
          name: 'Update Database',
          status: 'pending',
        },
      ]);
    } else if (diagnosis.recommendedAction === 'update' && diagnosis.canFix) {
      setStepProgress([
        {
          step: 1,
          name: 'Update Wallet SNSI',
          status: 'pending',
        },
        {
          step: 2,
          name: 'Mark Wallet as Registered',
          status: 'pending',
        },
      ]);
    }

    try {
      const res = await api.post('/wallet/failures/fix', {
        i_customer: diagnosis.walletICustomer,
        msisdn: walletFailures.find(f => f.i_customer === diagnosis.walletICustomer)?.msisdn,
      });

      const result = res.data;
      
      if (result.steps && Array.isArray(result.steps)) {
        setStepProgress(result.steps);
      }

      setFixResult({
        success: result.success || false,
        message: result.message || 'Unknown result',
      });

      if (result.success) {
        fetchWalletFailures();
      }
    } catch (err: any) {
      setFixResult({
        success: false,
        message: err.response?.data?.message || err.message || 'Failed to fix wallet',
      });
    } finally {
      setFixingId(null);
    }
  };

  const getActionColor = (action: string) => {
    switch (action) {
      case 'update':
        return 'success';
      case 'close':
        return 'warning';
      case 'deactivate':
        return 'warning';
      case 'ported':
        return 'info';
      case 'error':
        return 'error';
      default:
        return 'default';
    }
  };

  const getActionLabel = (action: string) => {
    switch (action) {
      case 'update':
        return 'Update Wallet';
      case 'close':
        return 'Close Account';
      case 'deactivate':
        return 'Deactivate Consumer';
      case 'ported':
        return 'Ported (Not Supported)';
      case 'error':
        return 'Error';
      default:
        return 'Unknown';
    }
  };

  const getStepBgClass = (status: string) => {
    switch (status) {
      case 'completed':
        return 'bg-green-500';
      case 'in_progress':
        return 'bg-primary';
      case 'error':
        return 'bg-red-500';
      case 'skipped':
        return 'bg-gray-400';
      default:
        return 'bg-gray-400';
    }
  };

  const renderStep = (step: any, index: number, isSubStep = false) => (
    <div key={index}>
      <div className="flex items-start gap-3">
        <div
          className={cn(
            'flex shrink-0 items-center justify-center rounded-full text-white font-bold',
            getStepBgClass(step.status),
            isSubStep ? 'size-6 text-xs' : 'size-8 text-sm',
          )}
        >
          {step.step}
        </div>
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-1">
            <span className="text-sm font-medium">{step.name}</span>
            {step.status === 'in_progress' && (
              <IconLoader2 className="size-4 animate-spin" />
            )}
            {step.status === 'completed' && (
              <IconCircleCheck className="size-4 text-green-500" />
            )}
            {step.status === 'error' && (
              <IconAlertCircle className="size-4 text-red-500" />
            )}
          </div>
          <span className="block text-xs text-muted-foreground mb-2">
            {diagnosis?.actionExplanations?.explanations?.find((e: any) => e.step === step.step)?.explanation || step.description || step.name}
          </span>
          {step.apiResponse && (
            <div className="mt-2 rounded bg-muted p-2">
              <span className="text-xs font-medium text-muted-foreground">
                API Response:
              </span>
              <pre className="mt-1 font-mono text-[0.7rem] whitespace-pre-wrap break-all">
                {JSON.stringify(step.apiResponse, null, 2)}
              </pre>
            </div>
          )}
          {step.error && (
            <Alert variant="destructive" className="mt-2">
              <IconAlertCircle />
              <AlertDescription>{step.error}</AlertDescription>
            </Alert>
          )}
        </div>
      </div>
    </div>
  );

  const renderActionButton = (label: string, variant: 'default' | 'outline' | 'destructive' = 'default', className?: string) => (
    <div className="mt-6 flex justify-end">
      <Button
        onClick={handleResolve}
        variant={variant}
        className={className}
        disabled={!!fixingId || !!fixResult?.success || !diagnosis?.canFix}
        title={!diagnosis?.canFix ? 'This action cannot be executed automatically. Please review the diagnosis details.' : ''}
      >
        {fixingId ? (
          <IconLoader2 className="size-4 animate-spin" />
        ) : (
          <IconCircleCheck className="size-4" />
        )}
        {fixingId
          ? 'Executing...'
          : fixResult?.success
            ? 'Completed'
            : label}
      </Button>
    </div>
  );

  return (
    <div>
      <PageHeader
        title="Wallet Failures"
        subtitle="View all customers where wallet creation has failed"
      />

      <MainCard className="mb-4">
        <div className="flex items-center justify-between mb-4">
          <h6 className="text-lg font-semibold">
            Wallet Failures ({walletFailures.length})
          </h6>
          <Button
            variant="outline"
            onClick={fetchWalletFailures}
            disabled={failuresLoading}
          >
            {failuresLoading ? (
              <IconLoader2 className="size-4 animate-spin" />
            ) : (
              <IconRefresh className="size-4" />
            )}
            Refresh
          </Button>
        </div>

        {failuresError && (
          <Alert variant="destructive" className="mb-4">
            <IconAlertCircle />
            <AlertDescription>{failuresError}</AlertDescription>
          </Alert>
        )}

        {failuresLoading ? (
          <div className="flex justify-center p-8">
            <IconLoader2 className="size-8 animate-spin text-muted-foreground" />
          </div>
        ) : walletFailures.length > 0 ? (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Customer ID</TableHead>
                <TableHead>MSISDN</TableHead>
                <TableHead>MSISDN DMA</TableHead>
                <TableHead>SIM Serial</TableHead>
                <TableHead>Error Reason</TableHead>
                <TableHead>Created At</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {walletFailures.map((failure, index) => {
                const isDiagnosing = diagnosingId === failure.i_customer;
                return (
                  <TableRow key={index}>
                    <TableCell>{failure.i_customer}</TableCell>
                    <TableCell>{failure.msisdn}</TableCell>
                    <TableCell>{failure.msisdn_dma || 'N/A'}</TableCell>
                    <TableCell>{failure.sim_serial_number || 'N/A'}</TableCell>
                    <TableCell>
                      <span className="font-medium text-destructive">
                        {failure.wallet_error_reason || 'Unknown error'}
                      </span>
                    </TableCell>
                    <TableCell>
                      {failure.created_at 
                        ? new Date(failure.created_at).toLocaleString()
                        : 'N/A'}
                    </TableCell>
                    <TableCell>
                      <Button
                        variant="default"
                        size="sm"
                        onClick={() => handleDiagnose(failure.i_customer, failure.msisdn)}
                        disabled={isDiagnosing}
                      >
                        {isDiagnosing ? (
                          <IconLoader2 className="size-4 animate-spin" />
                        ) : (
                          <IconTool className="size-4" />
                        )}
                        {isDiagnosing ? 'Diagnosing...' : 'Fix'}
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        ) : (
          <Alert>
            <AlertDescription>
              No wallet failures found. All wallets are healthy!
            </AlertDescription>
          </Alert>
        )}
      </MainCard>

      {/* Diagnosis Modal */}
      <Dialog
        open={!!diagnosis || diagnosisLoading}
        onOpenChange={(open) => {
          if (!open && !(fixingId && !fixResult)) handleCloseModal();
        }}
      >
        <DialogContent
          className="sm:max-w-3xl grid-rows-[auto_1fr_auto] max-h-[85vh]"
          showCloseButton={!(!!fixingId && !fixResult)}
        >
          <DialogHeader>
            <div className="flex items-center gap-2">
              <IconTool className="size-5" />
              <DialogTitle>Wallet Failure Diagnosis</DialogTitle>
            </div>
            <DialogDescription className="sr-only">
              Diagnose and resolve wallet creation failures
            </DialogDescription>
          </DialogHeader>

          <div className="overflow-y-auto -mx-4 px-4">
            {diagnosisLoading ? (
              <div className="flex items-center justify-center gap-3 py-8">
                <IconLoader2 className="size-6 animate-spin text-muted-foreground" />
                <span className="text-muted-foreground">Analyzing wallet failure...</span>
              </div>
            ) : diagnosisError ? (
              <Alert variant="destructive">
                <IconAlertCircle />
                <AlertDescription>{diagnosisError}</AlertDescription>
              </Alert>
            ) : diagnosis ? (
              <div className="space-y-4">
                {/* Customer Information */}
                <div>
                  <p className="text-sm font-medium text-muted-foreground mb-2">
                    Customer Information
                  </p>
                  <div className="flex gap-6 mt-1">
                    <div>
                      <span className="block text-xs text-muted-foreground">Wallet Customer ID</span>
                      <span className="text-sm font-medium">{diagnosis.walletICustomer}</span>
                    </div>
                    {diagnosis.apiICustomer && (
                      <div>
                        <span className="block text-xs text-muted-foreground">API Customer ID</span>
                        <span className="text-sm font-medium">{diagnosis.apiICustomer}</span>
                      </div>
                    )}
                  </div>
                </div>

                <Separator />

                {/* Status Information */}
                <div>
                  <p className="text-sm font-medium text-muted-foreground mb-2">
                    Status
                  </p>
                  <div className="flex flex-wrap gap-2 mt-1">
                    <Badge variant={diagnosis.matches ? 'success' : 'warning'}>
                      {diagnosis.matches ? 'IDs Match' : 'IDs Mismatch'}
                    </Badge>
                    {diagnosis.walletCustomerStatus && (
                      <Badge
                        variant={diagnosis.walletCustomerStatus === 'churned' ? 'destructive' : 'secondary'}
                      >
                        Wallet Customer: {diagnosis.walletCustomerStatus}
                      </Badge>
                    )}
                    {diagnosis.apiCustomerStatus && (
                      <Badge
                        variant={diagnosis.apiCustomerStatus === 'churned' ? 'destructive' : 'secondary'}
                      >
                        API Customer: {diagnosis.apiCustomerStatus}
                      </Badge>
                    )}
                  </div>
                </div>

                {diagnosis.walletAccountNumber && (
                  <>
                    <Separator />
                    <div>
                      <p className="text-sm font-medium text-muted-foreground mb-2">
                        Wallet Account
                      </p>
                      <div className="flex gap-6 mt-1">
                        <div>
                          <span className="block text-xs text-muted-foreground">Account Number</span>
                          <span className="text-sm font-medium">{diagnosis.walletAccountNumber}</span>
                        </div>
                        {diagnosis.accountBalance !== null && (
                          <div>
                            <span className="block text-xs text-muted-foreground">Balance</span>
                            <span className="text-sm font-medium">
                              R{parseFloat(diagnosis.accountBalance!).toFixed(2)}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                  </>
                )}

                <Separator />

                {/* Priority Badge */}
                {diagnosis.priority && (
                  <div>
                    <div className="flex items-center gap-3">
                      <Badge
                        variant={
                          diagnosis.priority.priority === 'critical'
                            ? 'destructive'
                            : diagnosis.priority.priority === 'high'
                              ? 'warning'
                              : diagnosis.priority.priority === 'medium'
                                ? 'info'
                                : 'secondary'
                        }
                      >
                        Priority: {diagnosis.priority.priority.toUpperCase()}
                      </Badge>
                      <span className="text-xs text-muted-foreground">
                        Score: {diagnosis.priority.score}/100
                      </span>
                    </div>
                    <p className="mt-2 text-sm text-muted-foreground">
                      {diagnosis.priority.explanation}
                    </p>
                    {diagnosis.priority.factors.length > 0 && (
                      <p className="mt-1 text-xs text-muted-foreground">
                        Factors: {diagnosis.priority.factors.join(', ')}
                      </p>
                    )}
                  </div>
                )}

                <Separator />

                {/* AI Summary */}
                {diagnosis.aiSummary && (
                  <div>
                    <p className="text-sm font-medium text-muted-foreground mb-2">
                      AI Analysis Summary
                    </p>
                    <div className="mt-1 rounded-md bg-muted p-3">
                      {diagnosis.aiSummary.split('\n').map((line: string, idx: number) => (
                        <p key={idx} className="text-sm mb-1 last:mb-0">
                          {line}
                        </p>
                      ))}
                    </div>
                  </div>
                )}

                <Separator />

                {/* Action Message */}
                <Alert
                  className={cn(
                    diagnosis.canFix
                      ? 'border-blue-200 bg-blue-50 text-blue-900 [&>svg]:text-blue-600'
                      : 'border-amber-200 bg-amber-50 text-amber-900 [&>svg]:text-amber-600'
                  )}
                >
                  {diagnosis.canFix ? <IconCircleCheck /> : <IconCircleX />}
                  <AlertDescription>{diagnosis.actionDetails}</AlertDescription>
                </Alert>

                {/* Overall Action Explanation */}
                {diagnosis.actionExplanations?.overallExplanation && (
                  <div>
                    <p className="text-sm font-medium text-muted-foreground mb-2">
                      Process Overview
                    </p>
                    <div className="mt-1 rounded-md bg-blue-50 p-3 text-blue-900">
                      <p className="text-sm">
                        {diagnosis.actionExplanations.overallExplanation}
                      </p>
                    </div>
                  </div>
                )}

                {/* Close Account Steps */}
                {diagnosis.recommendedAction === 'close' && diagnosis.canFix && (
                  <div>
                    <p className="text-sm font-medium text-muted-foreground mb-2">
                      Action Steps
                    </p>
                    <div className="space-y-4 mt-2">
                      {(stepProgress.length > 0 ? stepProgress : [
                        {
                          step: 1,
                          name: 'Check Account Balance',
                          status: 'pending' as const,
                          description: `Call Silicon API GET /network/express/account/${diagnosis.walletAccountNumber || '{churned_customer_account_number}'} to retrieve current account balance and available funds for the churned customer's wallet account`,
                        },
                        {
                          step: 2,
                          name: 'Transfer Funds to Holding Account',
                          status: 'pending' as const,
                          description: `If balance > 0, transfer all funds from account ${diagnosis.walletAccountNumber || '{churned_customer_account_number}'} to holding account 3000000234 using POST /network/express/account/transfer`,
                        },
                        {
                          step: 3,
                          name: 'Close Wallet Account',
                          status: 'pending' as const,
                          description: `Close the churned customer's wallet account ${diagnosis.walletAccountNumber || '{churned_customer_account_number}'} on Silicon using POST /network/express/account/close`,
                        },
                        {
                          step: 4,
                          name: 'Update Database',
                          status: 'pending' as const,
                          description: `Mark churned customer (i_customer: ${diagnosis.apiICustomer || '{api_i_customer}'}) wallet as closed (wallet_closed = 1). Clear wallet_error and wallet_error_reason to NULL on wallet failure record (i_customer: ${diagnosis.walletICustomer}) for cron reprocessing`,
                        },
                      ]).map((step: any, index: number) => {
                        const isSubStep = step.step === 2 && diagnosis.walletAccountNumber && diagnosis.accountBalance && parseFloat(diagnosis.accountBalance) > 0 && step.name === 'Transfer Funds to Holding Account';
                        return renderStep(step, index, !!isSubStep);
                      })}
                    </div>
                    {renderActionButton('Close Account', 'outline')}
                  </div>
                )}

                {/* Deactivate Consumer Steps */}
                {diagnosis.recommendedAction === 'deactivate' && diagnosis.canFix && (
                  <div>
                    <p className="text-sm font-medium text-muted-foreground mb-2">
                      Action Steps
                    </p>
                    <div className="space-y-4 mt-2">
                      {(stepProgress.length > 0 ? stepProgress : [
                        {
                          step: 1,
                          name: 'Deactivate Consumer on Silicon',
                          status: 'pending' as const,
                          description: `Deactivate the consumer on Silicon using PATCH /network/express/consumer/${walletFailures.find(f => f.i_customer === diagnosis.walletICustomer)?.msisdn || '{msisdn}'} to set state to "inactive". This will free up the MSISDN for the new customer.`,
                        },
                        {
                          step: 2,
                          name: 'Update Database',
                          status: 'pending' as const,
                          description: `Mark churned customer (i_customer: ${diagnosis.apiICustomer || '{api_i_customer}'}) wallet as closed (wallet_closed = 1). Clear wallet_error and wallet_error_reason to NULL on wallet failure record (i_customer: ${diagnosis.walletICustomer}) for cron reprocessing`,
                        },
                      ]).map((step: any, index: number) => renderStep(step, index))}
                    </div>
                    {renderActionButton('Deactivate Consumer', 'outline')}
                  </div>
                )}

                {/* Update Wallet Steps */}
                {diagnosis.recommendedAction === 'update' && diagnosis.canFix && (
                  <div>
                    <p className="text-sm font-medium text-muted-foreground mb-2">
                      Action Steps
                    </p>
                    <div className="space-y-4 mt-2">
                      {(stepProgress.length > 0 ? stepProgress : [
                        {
                          step: 1,
                          name: 'Update Wallet SNSI',
                          status: 'pending' as const,
                          description: `Update customers_wallets table: Set wallet_snsi = "${diagnosis.apiSnsi}" for customer ${diagnosis.walletICustomer}`,
                        },
                        {
                          step: 2,
                          name: 'Mark Wallet as Registered',
                          status: 'pending' as const,
                          description: `Update customers_wallets table: Set wallet_registered = 1, wallet_error = NULL, wallet_error_reason = NULL for customer ${diagnosis.walletICustomer} to allow cron reprocessing`,
                        },
                      ]).map((step: any, index: number) => renderStep(step, index))}
                    </div>
                    {renderActionButton('Update Wallet')}
                  </div>
                )}

                {diagnosis.apiSnsi && (
                  <>
                    <Separator />
                    <div>
                      <p className="text-sm font-medium text-muted-foreground mb-2">
                        API SNSI
                      </p>
                      <p className="mt-1 font-mono text-sm">
                        {diagnosis.apiSnsi}
                      </p>
                    </div>
                  </>
                )}

                {/* Fix Result */}
                {fixResult && (
                  <>
                    <Separator />
                    <Alert
                      variant={fixResult.success ? undefined : 'destructive'}
                      className={cn(
                        fixResult.success && 'border-green-200 bg-green-50 text-green-900 [&>svg]:text-green-600'
                      )}
                    >
                      {fixResult.success ? <IconCircleCheck /> : <IconAlertCircle />}
                      <AlertDescription>{fixResult.message}</AlertDescription>
                    </Alert>
                  </>
                )}
              </div>
            ) : null}
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={handleCloseModal}
              disabled={!!fixingId && !fixResult}
            >
              {fixResult?.success ? 'Close & Refresh' : 'Close'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
